/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import type { CustomFilterProps } from 'ag-grid-react'
import { useGridFilter } from 'ag-grid-react'
import { Checkbox } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import Button from 'src/components/ts-components/Button'
import Text from 'src/components/ts-components/Text'
import {
  passesSetFilter,
  toggleSetFilterValue,
  type SetFilterModel,
  type SetFilterOption,
} from './setFilter'

export interface SetColumnFilterParams {
  options: SetFilterOption[]
}

type SetColumnFilterProps<TRow> = CustomFilterProps<
  TRow,
  unknown,
  NonNullable<SetFilterModel>
> &
  SetColumnFilterParams

/**
 * A tick-list column filter. AG Grid's own set filter is Enterprise, so this is
 * the Community stand-in. Set it as a column's `filter`, with the choices in
 * `filterParams.options`; a row matches on the column's `valueGetter`.
 *
 * Ticks apply straight away, so the popup has no OK step.
 */
const SetColumnFilter = <TRow,>({
  model,
  onModelChange,
  getValue,
  options,
}: SetColumnFilterProps<TRow>) => {
  'use memo'

  useGridFilter({
    doesFilterPass: ({ node }) => passesSetFilter(model, getValue(node)),
  })

  return (
    <div css={panelStyle}>
      <div css={listStyle}>
        {options.map((option) => (
          <Checkbox
            key={option.value}
            checked={model?.includes(option.value) ?? false}
            onChange={() =>
              onModelChange(toggleSetFilterValue(model, option.value))
            }
          >
            <Text
              variant='paragraphSmall'
              weight='regular'
              color={colors.WHITE}
            >
              {option.label}
            </Text>
          </Checkbox>
        ))}
      </div>
      <Button
        size='compact'
        variant='outlined'
        text={capitalizeWords('Reset')}
        disabled={model === null}
        onClick={() => onModelChange(null)}
      />
    </div>
  )
}

export default SetColumnFilter

// ─── Styles ──────────────────────────────────────────────────────

const panelStyle = css`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  max-width: 320px;
  padding: 12px;
`

const listStyle = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 240px;
  overflow-y: auto;
`
